import React, { useEffect, useRef, useState } from "react";
import api, { API } from "../lib/api";
import Layout from "../components/Layout";
import { MessageCircle, Send, RefreshCw, Paperclip, Zap, Trash2, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import WaQueuePanel from "./WaQueuePanel";

const fmtTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const today = new Date();
  const same = d.toDateString() === today.toDateString();
  return same
    ? d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) + " " + d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
};

export default function WhatsAppInbox() {
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null); // chat_id
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionStatus, setSessionStatus] = useState("");
  const [shortcuts, setShortcuts] = useState([]);
  const [scOpen, setScOpen] = useState(false);
  const [scQuery, setScQuery] = useState("");
  const [queueOpen, setQueueOpen] = useState(false);
  const [queuePending, setQueuePending] = useState(0);
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const loadChats = async () => {
    try {
      const { data } = await api.get("/wa/chats");
      setChats(data);
    } catch (_) { /* poll berikutnya */ }
    setLoading(false);
  };

  const loadThread = async (chatId, scroll = true) => {
    try {
      const { data } = await api.get(`/wa/chats/${encodeURIComponent(chatId)}/messages`);
      if (activeRef.current === chatId) {
        setThread(data);
        if (scroll) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "auto" }), 50);
      }
    } catch (_) { /* poll berikutnya */ }
  };

  const loadSessionStatus = async () => {
    try {
      const { data } = await api.get("/wa/session/status");
      setSessionStatus(data.status || "");
    } catch (_) {}
    try {
      const { data } = await api.get("/wa/queue");
      setQueuePending(data?.stats?.pending || 0);
    } catch (_) {}
  };

  useEffect(() => {
    loadChats();
    loadSessionStatus();
    api.get("/wa/shortcuts").then((r) => setShortcuts(r.data || [])).catch(() => {});
    const iv = setInterval(() => {
      loadChats();
      if (activeRef.current) loadThread(activeRef.current, false);
    }, 8000);
    const ivSession = setInterval(loadSessionStatus, 30000);
    return () => { clearInterval(iv); clearInterval(ivSession); };
  }, []);

  const openChat = (chatId) => {
    setActive(chatId);
    setThread(null);
    loadThread(chatId);
    setChats((p) => p.map((c) => (c.chat_id === chatId ? { ...c, unread: 0 } : c)));
  };

  const send = async () => {
    const t = text.trim();
    if (!t || !active || sending) return;
    setSending(true);
    try {
      const { data: msg } = await api.post(`/wa/chats/${encodeURIComponent(active)}/send`, { text: t });
      setText("");
      setThread((p) => (p ? { ...p, messages: [...p.messages, msg] } : p));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      loadChats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim pesan", { duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  const fileRef = useRef(null);

  const deleteMessage = async (m) => {
    if (!window.confirm(m.from_me ? "Hapus pesan ini? Pesan juga akan dihapus di WhatsApp penerima (jika masih memungkinkan)." : "Hapus pesan ini dari inbox?")) return;
    try {
      const { data } = await api.delete(`/wa/chats/${encodeURIComponent(active)}/messages/${m.id}`);
      setThread((p) => (p ? { ...p, messages: p.messages.filter((x) => x.id !== m.id) } : p));
      if (data.wa_deleted) toast.success("Pesan dihapus dari WhatsApp ✓");
      else if (data.wa_error) toast.warning(`Dihapus dari inbox. ${data.wa_error}`, { duration: 7000 });
      else toast.success("Pesan dihapus dari inbox");
      loadChats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus pesan");
    }
  };

  const sendMedia = async (file) => {
    if (!file || !active || sending) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Ukuran file maksimal 15 MB"); return; }
    setSending(true);
    const tid = toast.loading(`Mengirim ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", text.trim());
      const { data: msg } = await api.post(`/wa/chats/${encodeURIComponent(active)}/send-media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText("");
      setThread((p) => (p ? { ...p, messages: [...p.messages, msg] } : p));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      toast.success("Media terkirim via WAHA ✓", { id: tid });
      loadChats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim media", { id: tid, duration: 8000 });
    } finally {
      setSending(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const activeChat = chats.find((c) => c.chat_id === active);
  const title = thread?.name || activeChat?.name || thread?.phone || activeChat?.phone || "";

  return (
    <Layout>
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight flex items-center gap-2">
            <MessageCircle className="text-[#16A34A]" size={28} /> WHATSAPP
          </h1>
          <p className="text-xs text-[#52525B] mt-1">Pesan masuk & keluar via WAHA. Balas langsung dari sini — diperbarui otomatis setiap 8 detik.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="wa-queue-open" onClick={() => setQueueOpen(true)} className="px-3 py-2 border border-[#E4E4E7] bg-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#F4F4F5] relative">
            <ListOrdered size={13} /> Antrian
            {queuePending > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#D97706] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full" data-testid="wa-queue-badge">{queuePending}</span>}
          </button>
          <button data-testid="wa-inbox-refresh" onClick={() => { loadChats(); if (active) loadThread(active, false); }} className="px-3 py-2 border border-[#E4E4E7] bg-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#F4F4F5]">
            <RefreshCw size={13} /> Muat Ulang
          </button>
        </div>
      </div>

      {queueOpen && <WaQueuePanel onClose={() => setQueueOpen(false)} />}

      {sessionStatus && sessionStatus !== "WORKING" && (
        <div className="mb-3 px-4 py-3 border border-[#DC2626]/30 bg-[#DC2626]/5 text-[#DC2626] text-xs font-bold flex items-center gap-2 flex-wrap" data-testid="wa-session-warning">
          <span>⚠ Sesi WhatsApp tidak aktif (status: {sessionStatus}) — pesan masuk TIDAK akan terupdate. Buka Pengaturan → WhatsApp Gateway lalu scan ulang QR.</span>
          <a href="/settings" className="underline">Buka Pengaturan</a>
        </div>
      )}

      <div className="bg-white border border-[#E4E4E7] flex" style={{ height: "calc(100vh - 210px)", minHeight: 420 }}>
        {/* Daftar chat */}
        <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-[320px] md:min-w-[320px] border-r border-[#E4E4E7] flex-col`}>
          <div className="px-4 py-3 border-b border-[#E4E4E7] label-cap text-[10px]">Percakapan ({chats.length})</div>
          <div className="flex-1 overflow-y-auto" data-testid="wa-chat-list">
            {loading ? (
              <div className="p-6 text-center text-xs text-[#52525B]">Memuat…</div>
            ) : chats.length === 0 ? (
              <div className="p-6 text-center text-xs text-[#52525B]" data-testid="wa-chat-empty">
                Belum ada pesan. Pesan yang terkirim / diterima via WAHA akan muncul di sini otomatis.
              </div>
            ) : chats.map((c) => (
              <button key={c.chat_id} data-testid={`wa-chat-item-${c.phone}`} onClick={() => openChat(c.chat_id)}
                className={`w-full text-left px-4 py-3 border-b border-[#E4E4E7]/60 hover:bg-[#F4F4F5] ${active === c.chat_id ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-sm truncate">{c.name || `+${c.phone}`}</div>
                  <div className="text-[10px] text-[#A1A1AA] font-mono whitespace-nowrap">{fmtTime(c.last_ts)}</div>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <div className="text-xs text-[#52525B] truncate">{c.last_from_me ? "Anda: " : ""}{c.last_body}</div>
                  {c.unread > 0 && <span data-testid={`wa-chat-unread-${c.phone}`} className="bg-[#16A34A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{c.unread}</span>}
                </div>
                {c.name && <div className="text-[10px] text-[#A1A1AA] font-mono">+{c.phone}</div>}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`${active ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#A1A1AA]">Pilih percakapan di kiri</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2">
                <button className="md:hidden text-xs font-bold text-[#002FA7] mr-1" onClick={() => setActive(null)} data-testid="wa-thread-back">←</button>
                <div>
                  <div className="font-bold text-sm" data-testid="wa-thread-title">{title}</div>
                  <div className="text-[10px] text-[#A1A1AA] font-mono">+{thread?.phone || activeChat?.phone}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-[#FAFAFA]" data-testid="wa-thread-messages">
                {!thread ? (
                  <div className="text-center text-xs text-[#52525B] py-6">Memuat…</div>
                ) : thread.messages.map((m) => (
                  <div key={m.id} className={`group flex items-center gap-1 ${m.from_me ? "justify-end" : "justify-start"}`}>
                    {m.from_me && (
                      <button
                        onClick={() => deleteMessage(m)}
                        data-testid={`wa-msg-delete-${m.id}`}
                        title="Hapus pesan"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-[#DC2626] hover:bg-[#DC2626]/10 border border-transparent hover:border-[#DC2626]/20"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                    <div className={`max-w-[75%] px-3 py-2 text-sm whitespace-pre-wrap break-words ${m.from_me ? "bg-[#DCF8C6] border border-[#c6ecb0]" : "bg-white border border-[#E4E4E7]"}`}>
                      {m.media_file && (m.media_mime || "").startsWith("image/") && (
                        <img
                          data-testid={`wa-media-img-${m.id}`}
                          src={`${API}/wa/media/${m.media_file}`}
                          alt="media"
                          loading="lazy"
                          className="max-w-full w-64 border border-[#E4E4E7] mb-1.5 cursor-pointer bg-white"
                          onClick={() => window.open(`${API}/wa/media/${m.media_file}`, "_blank")}
                        />
                      )}
                      {m.media_file && !(m.media_mime || "").startsWith("image/") && (
                        <a href={`${API}/wa/media/${m.media_file}`} target="_blank" rel="noreferrer" className="block text-[#002FA7] underline text-xs mb-1">📎 Buka / unduh file</a>
                      )}
                      {m.body}
                      <div className="text-[9px] text-[#A1A1AA] font-mono text-right mt-1">{fmtTime(m.ts)}{m.from_me ? " ✓" : ""}</div>
                    </div>
                    {!m.from_me && (
                      <button
                        onClick={() => deleteMessage(m)}
                        data-testid={`wa-msg-delete-${m.id}`}
                        title="Hapus pesan dari inbox"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-[#DC2626] hover:bg-[#DC2626]/10 border border-transparent hover:border-[#DC2626]/20"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="border-t border-[#E4E4E7] p-3 flex items-end gap-2 relative">
                {scOpen && (
                  <div className="absolute bottom-full left-3 right-3 mb-1 bg-white border border-[#E4E4E7] shadow-lg z-30 max-h-64 overflow-y-auto" data-testid="wa-shortcut-dropdown">
                    <div className="px-3 py-2 border-b border-[#E4E4E7] flex items-center gap-2 sticky top-0 bg-white">
                      <Zap size={12} className="text-[#16A34A]" />
                      <input
                        autoFocus
                        value={scQuery}
                        onChange={(e) => setScQuery(e.target.value)}
                        placeholder="Cari shortcut…"
                        className="flex-1 outline-none text-xs"
                        data-testid="wa-shortcut-search"
                      />
                      <button onClick={() => setScOpen(false)} className="text-[#52525B] text-xs">✕</button>
                    </div>
                    {shortcuts.filter((s) => !scQuery || s.name.toLowerCase().includes(scQuery.toLowerCase()) || s.message.toLowerCase().includes(scQuery.toLowerCase())).map((s) => (
                      <button
                        key={s.id}
                        data-testid={`wa-shortcut-option-${s.id}`}
                        onClick={() => {
                          const nm = (title || "").trim();
                          const isPhone = !nm || /^[+\d\s-]+$/.test(nm);
                          const msg = s.message.replaceAll("{nama}", isPhone ? "Kak" : nm);
                          setText((t) => (t ? t + " " + msg : msg));
                          setScOpen(false);
                          setScQuery("");
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-[#F4F4F5] border-b border-[#E4E4E7]/60 last:border-b-0"
                      >
                        <div className="text-xs font-bold text-[#16A34A]">/{s.name}</div>
                        <div className="text-[11px] text-[#52525B] line-clamp-2 whitespace-pre-wrap">{s.message}</div>
                      </button>
                    ))}
                    {shortcuts.length === 0 && (
                      <div className="px-3 py-3 text-xs text-[#52525B]">Belum ada shortcut. Buat di menu <b>WhatsApp → Shortcut Pesan</b>.</div>
                    )}
                  </div>
                )}
                <input ref={fileRef} type="file" className="hidden" data-testid="wa-media-input" onChange={(e) => sendMedia(e.target.files?.[0])} />
                <button data-testid="wa-attach-btn" onClick={() => fileRef.current?.click()} disabled={sending} title="Kirim foto / file (teks di kotak jadi caption)" className="px-3 py-2.5 border border-[#E4E4E7] bg-white hover:bg-[#F4F4F5] disabled:opacity-50">
                  <Paperclip size={16} />
                </button>
                <button data-testid="wa-shortcut-btn" onClick={() => { setScOpen((v) => !v); setScQuery(""); }} disabled={sending} title="Sisipkan shortcut pesan" className={`px-3 py-2.5 border bg-white hover:bg-[#F4F4F5] disabled:opacity-50 ${scOpen ? "border-[#16A34A] text-[#16A34A]" : "border-[#E4E4E7]"}`}>
                  <Zap size={16} />
                </button>
                <textarea
                  data-testid="wa-reply-input"
                  value={text}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "/") { setScOpen(true); setScQuery(""); setText(""); return; }
                    setText(v);
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Tulis balasan… (Enter kirim, ketik / untuk shortcut)"
                  rows={2}
                  className="flex-1 px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] resize-none"
                />
                <button data-testid="wa-reply-send" onClick={send} disabled={sending || !text.trim()} className="px-4 py-2.5 bg-[#16A34A] text-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#15803D] disabled:opacity-50">
                  <Send size={14} /> Kirim
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
